<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import PipelinesAPI from 'dashboard/api/pipelines';
import Button from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';
import { default as FluentIcon } from 'shared/components/FluentIcon/Icon.vue';
import dashboardIcons from 'shared/components/FluentIcon/dashboard-icons.json';
import { emitter } from 'shared/helpers/mitt';

import {
  getStageColorClasses,
  normalizePipelineTemplateData,
} from './pipelineTemplates';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  templates: {
    type: Array,
    default: () => [],
  },
  accountId: {
    type: [Number, String],
    default: null,
  },
  accountGeneration: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['close', 'pipeline-created', 'start-from-scratch']);
const { t } = useI18n();

const currentStep = ref('welcome');
const selectedTemplateId = ref('');
const creating = ref(false);
const creationError = ref('');
const stepContainerRef = ref(null);
let createRequestGeneration = 0;

const normalizedAccountId = () =>
  props.accountId === undefined || props.accountId === null
    ? null
    : String(props.accountId);
const captureAccountContext = () => ({
  accountId: normalizedAccountId(),
  generation: props.accountGeneration,
});
const accountContextIsCurrent = context =>
  context.accountId === normalizedAccountId() &&
  context.generation === props.accountGeneration;

const selectedTemplate = computed(() =>
  props.templates.find(template => template.id === selectedTemplateId.value)
);

const reset = () => {
  currentStep.value = 'welcome';
  selectedTemplateId.value = '';
  creationError.value = '';
};

const close = () => {
  if (creating.value) return;
  emit('close');
};

const focusStepElement = async selector => {
  await nextTick();
  stepContainerRef.value?.querySelector(selector)?.focus();
};

const handleChooseTemplate = async () => {
  creationError.value = '';
  currentStep.value = 'templates';
  await focusStepElement('[data-template-back]');
};

const handleStartFromScratch = () => {
  if (creating.value) return;
  /* eslint-disable-next-line vue/custom-event-name-casing -- public component event */
  emit('start-from-scratch');
};

const handleTemplateSelect = templateId => {
  selectedTemplateId.value = templateId;
  creationError.value = '';
};

const handleCreateFromTemplate = async () => {
  if (!selectedTemplate.value || creating.value) return;

  const accountContext = captureAccountContext();
  if (accountContext.accountId === null) {
    creationError.value = t('PIPELINE.ERRORS.CREATE_PIPELINE');
    emitter.emit('newToastMessage', {
      message: creationError.value,
      action: { type: 'error' },
    });
    return;
  }

  createRequestGeneration += 1;
  const requestGeneration = createRequestGeneration;
  const requestIsCurrent = () =>
    requestGeneration === createRequestGeneration &&
    accountContextIsCurrent(accountContext);

  creating.value = true;
  creationError.value = '';

  try {
    const payload = {
      ...normalizePipelineTemplateData(selectedTemplate.value),
      active: true,
    };

    const response = await PipelinesAPI.create(
      payload,
      accountContext.accountId
    );
    if (!requestIsCurrent()) return;

    emitter.emit('newToastMessage', {
      message: t('PIPELINE.WELCOME.SUCCESS_MESSAGE'),
      action: { type: 'success' },
    });

    /* eslint-disable-next-line vue/custom-event-name-casing -- public component event */
    emit('pipeline-created', response.data, accountContext);
    emit('close');
  } catch {
    if (!requestIsCurrent()) return;
    creationError.value = t('PIPELINE.ERRORS.CREATE_PIPELINE');
    emitter.emit('newToastMessage', {
      message: creationError.value,
      action: { type: 'error' },
    });
  } finally {
    if (requestGeneration === createRequestGeneration) {
      creating.value = false;
    }
  }
};

const handleBack = async () => {
  if (creating.value) return;
  currentStep.value = 'welcome';
  selectedTemplateId.value = '';
  creationError.value = '';
  await focusStepElement('[data-welcome-primary]');
};

watch(
  () => props.show,
  show => {
    if (show) reset();
  }
);

watch(
  [() => props.accountId, () => props.accountGeneration],
  () => {
    createRequestGeneration += 1;
    creating.value = false;
    reset();
  },
  { flush: 'sync' }
);
</script>

<template>
  <Modal
    :show="show"
    size="large"
    :show-close-button="!creating"
    :close-on-backdrop-click="!creating"
    :close-on-escape="!creating"
    initial-focus-selector="[data-welcome-primary]"
    @close="close"
  >
    <div
      v-if="currentStep === 'welcome'"
      ref="stepContainerRef"
      class="p-5 sm:p-8"
    >
      <div class="max-w-2xl mx-auto text-center">
        <div
          class="inline-flex items-center justify-center w-16 h-16 mb-5 rounded-2xl bg-n-brand/10 text-n-brand"
          aria-hidden="true"
        >
          <FluentIcon icon="briefcase" size="32" :icons="dashboardIcons" />
        </div>
        <h2
          data-modal-title
          class="text-2xl font-semibold text-n-slate-12 sm:text-3xl"
        >
          {{ t('PIPELINE.WELCOME.TITLE') }}
        </h2>
        <p class="mt-3 text-base leading-6 text-n-slate-10">
          {{ t('PIPELINE.WELCOME.DESCRIPTION') }}
        </p>
      </div>

      <div class="grid max-w-4xl grid-cols-1 gap-4 mx-auto mt-8 sm:grid-cols-2">
        <button
          type="button"
          data-welcome-primary
          class="min-h-48 p-5 text-left border rounded-xl border-n-weak bg-n-solid-2 outline-none transition-colors hover:border-n-brand hover:bg-n-brand/5 focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2 focus-visible:ring-offset-n-background"
          @click="handleChooseTemplate"
        >
          <span
            class="flex items-center justify-center w-12 h-12 rounded-xl bg-n-blue-3 text-n-blue-11"
            aria-hidden="true"
          >
            <FluentIcon icon="copy" size="24" :icons="dashboardIcons" />
          </span>
          <span class="block mt-4 text-lg font-semibold text-n-slate-12">
            {{ t('PIPELINE.WELCOME.USE_TEMPLATE') }}
          </span>
          <span class="block mt-2 text-sm leading-5 text-n-slate-10">
            {{ t('PIPELINE.WELCOME.USE_TEMPLATE_DESC') }}
          </span>
          <span
            class="inline-flex items-center gap-2 mt-4 text-sm font-medium text-n-blue-11"
          >
            {{ t('PIPELINE.WELCOME.CHOOSE_TEMPLATE') }}
            <FluentIcon
              icon="arrow-chevron-right"
              size="16"
              :icons="dashboardIcons"
              aria-hidden="true"
            />
          </span>
        </button>

        <button
          type="button"
          class="min-h-48 p-5 text-left border rounded-xl border-n-weak bg-n-solid-2 outline-none transition-colors hover:border-n-violet-8 hover:bg-n-violet-3 focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2 focus-visible:ring-offset-n-background"
          @click="handleStartFromScratch"
        >
          <span
            class="flex items-center justify-center w-12 h-12 rounded-xl bg-n-violet-3 text-n-violet-11"
            aria-hidden="true"
          >
            <FluentIcon icon="add-circle" size="24" :icons="dashboardIcons" />
          </span>
          <span class="block mt-4 text-lg font-semibold text-n-slate-12">
            {{ t('PIPELINE.WELCOME.START_FROM_SCRATCH') }}
          </span>
          <span class="block mt-2 text-sm leading-5 text-n-slate-10">
            {{ t('PIPELINE.WELCOME.START_FROM_SCRATCH_DESC') }}
          </span>
          <span
            class="inline-flex items-center gap-2 mt-4 text-sm font-medium text-n-violet-11"
          >
            {{ t('PIPELINE.WELCOME.CREATE_CUSTOM') }}
            <FluentIcon
              icon="arrow-chevron-right"
              size="16"
              :icons="dashboardIcons"
              aria-hidden="true"
            />
          </span>
        </button>
      </div>
    </div>

    <div v-else ref="stepContainerRef" class="p-5 sm:p-8">
      <div class="flex items-center gap-3 pr-12">
        <Button
          type="button"
          variant="ghost"
          color="slate"
          size="lg"
          icon="i-lucide-chevron-left"
          :aria-label="t('PIPELINE.WELCOME.BACK')"
          :title="t('PIPELINE.WELCOME.BACK')"
          :disabled="creating"
          data-template-back
          @click="handleBack"
        />
        <div>
          <h2
            data-modal-title
            class="text-xl font-semibold text-n-slate-12 sm:text-2xl"
          >
            {{ t('PIPELINE.WELCOME.SELECT_TEMPLATE') }}
          </h2>
          <p class="mt-1 text-sm text-n-slate-10">
            {{ t('PIPELINE.TEMPLATES_LIBRARY.SUBTITLE') }}
          </p>
        </div>
      </div>

      <div
        class="grid grid-cols-1 gap-3 mt-6 md:grid-cols-2"
        role="group"
        :aria-label="t('PIPELINE.TEMPLATES_LIBRARY.OPTIONS_LABEL')"
      >
        <button
          v-for="template in templates"
          :key="template.id"
          type="button"
          :aria-pressed="selectedTemplateId === template.id"
          class="min-h-32 p-4 text-left border rounded-xl outline-none transition-colors focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2 focus-visible:ring-offset-n-background"
          :class="
            selectedTemplateId === template.id
              ? 'border-n-brand bg-n-brand/10'
              : 'border-n-weak bg-n-solid-2 hover:border-n-strong hover:bg-n-alpha-1'
          "
          :disabled="creating"
          @click="handleTemplateSelect(template.id)"
        >
          <div class="flex items-start gap-3">
            <span
              class="flex items-center justify-center flex-shrink-0 w-11 h-11 rounded-xl bg-n-alpha-2 text-n-slate-11"
              aria-hidden="true"
            >
              <FluentIcon
                :icon="template.icon"
                size="21"
                :icons="dashboardIcons"
              />
            </span>
            <span class="min-w-0">
              <span class="block font-semibold text-n-slate-12">
                {{ template.name }}
              </span>
              <span class="block mt-1 text-sm leading-5 text-n-slate-10">
                {{ template.description }}
              </span>
            </span>
          </div>
          <span class="flex flex-wrap gap-1.5 mt-4" aria-hidden="true">
            <span
              v-for="stage in Object.values(template.stages)"
              :key="stage.id"
              class="inline-flex items-center px-2 py-1 text-xs font-medium border rounded-md"
              :class="getStageColorClasses(stage.color).badge"
            >
              {{ stage.name }}
            </span>
          </span>
          <span class="sr-only">
            {{
              t('PIPELINE.TEMPLATES_LIBRARY.STAGES_SUMMARY', {
                count: Object.keys(template.stages).length,
              })
            }}
          </span>
        </button>
      </div>

      <div
        v-if="creationError"
        role="alert"
        class="flex items-start gap-2 p-3 mt-4 text-sm border rounded-lg border-n-ruby-6 bg-n-ruby-3 text-n-ruby-11"
      >
        <span class="i-lucide-circle-alert flex-shrink-0 mt-0.5" />
        <span>{{ creationError }}</span>
      </div>

      <div class="flex flex-col-reverse gap-2 mt-6 sm:flex-row sm:justify-end">
        <Button
          type="button"
          variant="ghost"
          color="slate"
          size="lg"
          :disabled="creating"
          @click="handleBack"
        >
          {{ t('PIPELINE.WELCOME.BACK') }}
        </Button>
        <Button
          type="button"
          variant="solid"
          color="blue"
          size="lg"
          :is-loading="creating"
          :disabled="!selectedTemplate || creating"
          @click="handleCreateFromTemplate"
        >
          {{
            creating
              ? t('PIPELINE.WELCOME.CREATING')
              : t('PIPELINE.WELCOME.CREATE_PIPELINE')
          }}
        </Button>
      </div>
    </div>
  </Modal>
</template>
